import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import TopBar from "@/components/top-bar";
import CircularLoading from "@/components/circular-loading";
import Icon from "@/components/icon";
import Image from "@/components/image";
import Section from "@/components/section";
import { RowDivider } from "@/components/row";
import { TransactionService } from "@/services/transaction-service";
import {
  TransactionSource,
  type Transaction,
} from "@/models/transaction/Transaction";
import { useProfileStore } from "@/store/profile-store";

const PER_PAGE = 6;

const PURCHASE_PATH_LABELS: Record<TransactionSource, string> = {
  [TransactionSource.VITRIN]: "ویترین",
  [TransactionSource.MINIAPP_DIRECT]: "برنامک",
};

const fa = (n: number) => Number(n || 0).toLocaleString("fa-IR");

const splitDateTime = (iso: string) => {
  try {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString("fa-IR", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      }),
      time: d.toLocaleTimeString("fa-IR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    };
  } catch {
    return { date: iso, time: "" };
  }
};

export default function TransactionsPage() {
  const accessToken = useProfileStore((s) => s.tokens?.access_token);
  const [page, setPage] = useState(1);

  const { data, isPending, isError, isFetching } = useQuery({
    queryKey: ["my-transactions", page],
    queryFn: () =>
      TransactionService.getMyTransactions({
        page,
        per_page: PER_PAGE,
        sort: "-created_at",
      }),
    enabled: Boolean(accessToken),
  });

  const transactions = data?.data ?? [];
  const pagination = data?.pagination;
  const lastPage = pagination?.last_page ?? 1;
  const total = pagination?.total ?? 0;

  return (
    <div className="bg-secondary-background min-h-svh pb-24">
      <TopBar title="تراکنش‌ها" altHref="/profile" />

      {isPending ? (
        <CircularLoading />
      ) : isError ? (
        <p className="py-16 text-center text-sm text-danger">
          مشکلی در دریافت تراکنش‌ها پیش آمد.
        </p>
      ) : transactions.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-20 text-center">
          <span className="flex size-14 items-center justify-center rounded-2xl bg-content2">
            <Icon name="transactions" size={26} className="text-default-400" />
          </span>
          <p className="text-sm text-default-500">
            تراکنشی برای نمایش وجود ندارد.
          </p>
        </div>
      ) : (
        <>
          <Section title="تراکنش‌ها">
            {transactions.map((t, i) => (
              <div key={t.reference_key}>
                {i > 0 ? <RowDivider inset /> : null}
                <TransactionRow transaction={t} />
              </div>
            ))}
          </Section>

          {lastPage > 1 ? (
            <div className="fixed inset-x-0 bottom-5 z-20 flex justify-center px-4">
              <div className="flex items-center gap-1 rounded-full border border-default-100 bg-background p-1.5 shadow-lg shadow-default-900/10">
                <button
                  type="button"
                  aria-label="صفحه قبلی"
                  className="flex size-9 items-center justify-center rounded-full text-default-700 transition active:bg-content2 disabled:opacity-40"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || isFetching}
                >
                  <Icon name="chevron-right" size={18} />
                </button>

                <span className="min-w-[5.5rem] text-center text-xs font-medium text-default-700">
                  صفحه {fa(page)} از {fa(lastPage)}
                </span>

                <button
                  type="button"
                  aria-label="صفحه بعدی"
                  className="flex size-9 items-center justify-center rounded-full text-default-700 transition active:bg-content2 disabled:opacity-40"
                  onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                  disabled={page >= lastPage || isFetching}
                >
                  <Icon name="chevron-left" size={18} />
                </button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function TransactionRow({ transaction }: { transaction: Transaction }) {
  const navigate = useNavigate();
  const { amount, final_amount, source, created_at, course, mini_app, is_installment } =
    transaction;

  const { date, time } = splitDateTime(created_at);
  const isFree = final_amount === 0;
  const paid = final_amount ?? amount;
  const hasDiscount = final_amount !== null && final_amount < amount;

  const courseHref = `/course/${course?.id}?miniAppUuid=${encodeURIComponent(
    mini_app.uuid,
  )}`;

  return (
    <button
      type="button"
      onClick={() => navigate(courseHref)}
      dir="rtl"
      className="flex w-full items-center gap-3 px-4 py-3.5 text-right transition active:bg-content2"
    >
      {/* Course cover as the transaction avatar */}
      <div className="relative flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-content2">
        {course?.cover_url ? (
          <Image
            src={course.cover_url}
            alt={course.title}
            width={44}
            height={44}
            className="size-full object-cover"
          />
        ) : (
          <Icon name="lessons-fill" size={20} className="text-default-400" />
        )}
      </div>

      {/* What + where + tracking id */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold text-default-900">
          {course?.title || "دوره"}
        </p>
        <p className="mt-0.5 truncate text-[11px] text-default-500">
          {is_installment ? 'خرید قسطی' : 'خرید از درگاه'}
          {/* {`خرید دوره از ${PURCHASE_PATH_LABELS[source]}`} */}
        </p>
        <p className="mt-1 truncate text-xxs text-default-400" dir="ltr">
          {transaction.reference_key}
        </p>
      </div>

      {/* Amount + status + date */}
      <div className="flex shrink-0 flex-col items-end gap-1 self-end">
        {isFree ? (
          <span className="text-sm font-semibold text-success">رایگان</span>
        ) : (
          <span className="text-sm font-extrabold text-default-900">
            {fa(paid)}
            <span className="mr-1 text-xxs font-normal text-default-500">
              تومان
            </span>
          </span>
        )}
        {hasDiscount ? (
          <span className="text-xxs text-default-400 line-through">
            {fa(amount)}
          </span>
        ) : null}

        <span className="text-xxs text-default-400" dir="ltr">
          {date}
          {time ? ` · ${time}` : ""}
        </span>
      </div>
    </button>
  );
}
